import { FileText, FileSpreadsheet, TrendingUp, TrendingDown, Minus, Mail } from "lucide-react";
import { fmtMoney } from "@/lib/format";
import { useCompactMoney } from "@/lib/useCompactMoney";

/**
 * IncomeStatementView
 * -----------------------------------------------------------------
 * Formal multi-step Income Statement. Renders in one of two layouts:
 *
 *   1) Standalone (single column): when `data` is a regular IS payload
 *      ({revenue, cogs, gross_profit, operating_expenses, ...})
 *
 *   2) Comparative (4 columns): when `data` is a comparative payload
 *      ({current_payload, prior_payload, period_label, ... each line item
 *       carrying {current, prior, change, change_pct}}). Renders:
 *        Label | Current | Prior | Δ%
 */
export default function IncomeStatementView({ data, onExport, onShare, compareMode, onCompareModeChange }) {
  if (!data) return null;
  const isComparative = Boolean(data?.current_payload && data?.prior_payload);

  return (
    <div className="ll-card-flat p-6 sm:p-8" data-testid="income-statement-view">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5 pb-4 border-b" style={{ borderColor: "var(--ll-border)" }}>
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[var(--ll-ink-muted)]">
            Multi-step format{isComparative ? ` · vs ${data.period_label}` : ""}
          </div>
          <h3 className="font-display font-bold text-2xl mt-1">Income Statement</h3>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <select
            value={compareMode || ""}
            onChange={(e) => onCompareModeChange?.(e.target.value || null)}
            data-testid="is-compare-select"
            className="text-xs px-2 py-1.5 border bg-white"
            style={{ borderColor: "var(--ll-border)" }}
            title="Set start & end dates first for a meaningful comparison"
          >
            <option value="">No comparison</option>
            <option value="prior_period">vs prior period</option>
            <option value="prior_year">vs same period last year</option>
          </select>
          <button
            type="button"
            onClick={() => onExport?.("income-statement", "pdf")}
            data-testid="export-is-pdf"
            className="text-xs px-3 py-2 border bg-white hover:bg-[var(--ll-bg-sidebar)] flex items-center gap-1.5"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <FileText size={12} /> PDF
          </button>
          <button
            type="button"
            onClick={() => onExport?.("income-statement", "xlsx")}
            data-testid="export-is-xlsx"
            className="text-xs px-3 py-2 border bg-white hover:bg-[var(--ll-bg-sidebar)] flex items-center gap-1.5"
            style={{ borderColor: "var(--ll-border)" }}
          >
            <FileSpreadsheet size={12} /> Excel
          </button>
          {onShare && (
            <button
              type="button"
              onClick={() => onShare("income-statement")}
              data-testid="is-share-email"
              title="Share by email"
              className="text-xs px-3 py-2 bg-[#0b5d9e] text-white hover:opacity-90 flex items-center gap-1.5"
            >
              <Mail size={12} /> Share by email
            </button>
          )}
        </div>
      </div>

      {isComparative ? (
        <ComparativeBody data={data} />
      ) : (
        <StandaloneBody data={data} />
      )}
    </div>
  );
}

/* ---------------- Standalone (single-column) ---------------- */
function StandaloneBody({ data }) {
  return (
    <div className="max-w-2xl mx-auto font-mono-fin">
      <SectionHeader label="Revenue" />
      {data.revenue.length === 0 && <EmptyRow label="No revenue in this period" />}
      {data.revenue.map((r) => (
        <LineRow key={r.category} label={r.category} amount={r.amount} />
      ))}
      <SubtotalRow label="Total Revenue" amount={data.total_revenue} testid="is-total-revenue" />

      {data.cogs.length > 0 && (
        <>
          <SectionHeader label="Cost of Goods Sold" />
          {data.cogs.map((r) => (
            <LineRow key={r.category} label={r.category} amount={-r.amount} />
          ))}
          <SubtotalRow label="Total COGS" amount={-data.total_cogs} />
        </>
      )}

      <TotalRow
        label="Gross Profit"
        amount={data.gross_profit}
        margin={data.gross_margin_pct}
        marginLabel="gross margin"
        testid="is-gross-profit"
      />

      <SectionHeader label="Operating Expenses" />
      {data.operating_expenses.length === 0 && <EmptyRow label="No operating expenses in this period" />}
      {data.operating_expenses.map((r) => (
        <LineRow key={r.bucket} label={r.bucket} amount={-r.amount} sub={r.pct ? `${r.pct}% of revenue` : null} />
      ))}
      <SubtotalRow label="Total Operating Expenses" amount={-data.total_operating_expenses} />

      <TotalRow
        label="Operating Income"
        amount={data.operating_income}
        margin={data.operating_margin_pct}
        marginLabel="operating margin"
        testid="is-operating-income"
      />

      {(data.other_income > 0 || data.other_expense > 0) && (
        <>
          <SectionHeader label="Other Income / (Expense)" />
          {data.other_income > 0 && <LineRow label="Other income" amount={data.other_income} />}
          {data.other_expense > 0 && <LineRow label="Other expense" amount={-data.other_expense} />}
        </>
      )}

      <SubtotalRow label="Income Before Tax" amount={data.income_before_tax} testid="is-income-before-tax" />
      <LineRow
        label={`Estimated tax (${Math.round((data.estimated_tax_rate || 0) * 100)}%)`}
        amount={-data.estimated_tax}
        sub="Illustrative — replace with actual filing"
      />
      <NetIncomeRow amount={data.net_income} margin={data.net_margin_pct} />
    </div>
  );
}

/* ---------------- Comparative (4-column) ---------------- */
function ComparativeBody({ data }) {
  return (
    <div className="overflow-x-auto -mx-1" data-testid="is-comparative-body">
      <table className="w-full font-mono-fin text-sm border-separate" style={{ borderSpacing: 0 }}>
        <thead>
          <tr className="text-[10px] uppercase tracking-[0.2em] text-[var(--ll-ink-muted)]">
            <th className="text-left py-3 px-2 font-medium">Item</th>
            <th className="text-right py-3 px-2 font-medium">Current</th>
            <th className="text-right py-3 px-2 font-medium">{data.period_label}</th>
            <th className="text-right py-3 px-2 font-medium">Δ</th>
            <th className="text-right py-3 px-2 font-medium">Δ%</th>
          </tr>
        </thead>
        <tbody>
          <CmpSectionHeader label="Revenue" />
          {data.revenue.map((r) => <CmpLine key={`r-${r.category}`} label={r.category} cell={r} />)}
          <CmpSubtotal label="Total Revenue" cell={data.total_revenue} testid="is-total-revenue" />

          {data.cogs.length > 0 && (
            <>
              <CmpSectionHeader label="Cost of Goods Sold" />
              {data.cogs.map((r) => <CmpLine key={`c-${r.category}`} label={r.category} cell={r} negate />)}
              <CmpSubtotal label="Total COGS" cell={data.total_cogs} negate />
            </>
          )}

          <CmpTotal
            label="Gross Profit"
            cell={data.gross_profit}
            margin={data.gross_margin_pct}
            marginLabel="gross margin"
            testid="is-gross-profit"
          />

          <CmpSectionHeader label="Operating Expenses" />
          {data.operating_expenses.map((r) => <CmpLine key={`o-${r.bucket}`} label={r.bucket} cell={r} negate />)}
          <CmpSubtotal label="Total Operating Expenses" cell={data.total_operating_expenses} negate />

          <CmpTotal
            label="Operating Income"
            cell={data.operating_income}
            margin={data.operating_margin_pct}
            marginLabel="operating margin"
            testid="is-operating-income"
          />

          {(data.other_income.current > 0 || data.other_expense.current > 0 ||
            data.other_income.prior > 0 || data.other_expense.prior > 0) && (
            <>
              <CmpSectionHeader label="Other Income / (Expense)" />
              {(data.other_income.current > 0 || data.other_income.prior > 0) && (
                <CmpLine label="Other income" cell={data.other_income} />
              )}
              {(data.other_expense.current > 0 || data.other_expense.prior > 0) && (
                <CmpLine label="Other expense" cell={data.other_expense} negate />
              )}
            </>
          )}

          <CmpSubtotal label="Income Before Tax" cell={data.income_before_tax} testid="is-income-before-tax" />
          <CmpLine label="Estimated tax" cell={data.estimated_tax} negate />
          <CmpNetIncomeRow cell={data.net_income} margin={data.net_margin_pct} />
        </tbody>
      </table>
    </div>
  );
}

/* ----- Standalone row components ----- */
function SectionHeader({ label }) {
  return (
    <div className="text-[10px] uppercase tracking-[0.25em] text-white mt-6 mb-2 py-2 px-4" style={{ background: "#0b5d9e" }}>
      {label}
    </div>
  );
}

function LineRow({ label, amount, sub = null }) {
  const negative = amount < 0;
  return (
    <div className="flex items-baseline justify-between py-1.5 text-sm">
      <div className="min-w-0">
        <div className="truncate">{label}</div>
        {sub && <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mt-0.5">{sub}</div>}
      </div>
      <div className={negative ? "text-[var(--ll-error)]" : ""}>
        {negative ? `(${fmtMoney(Math.abs(amount))})` : fmtMoney(amount)}
      </div>
    </div>
  );
}

function EmptyRow({ label }) {
  return <div className="py-2 text-xs text-[var(--ll-ink-muted)] italic">{label}</div>;
}

function SubtotalRow({ label, amount, testid }) {
  const negative = amount < 0;
  return (
    <div
      className="flex items-baseline justify-between py-2 text-sm font-medium border-t mt-1"
      style={{ borderColor: "var(--ll-border)" }}
      data-testid={testid}
    >
      <span>{label}</span>
      <span className={negative ? "text-[var(--ll-error)]" : ""}>
        {negative ? `(${fmtMoney(Math.abs(amount))})` : fmtMoney(amount)}
      </span>
    </div>
  );
}

function TotalRow({ label, amount, margin, marginLabel, testid }) {
  const negative = amount < 0;
  return (
    <div className="my-4 py-3 px-4 border-y-2 text-white"
      style={{ borderColor: "#0b5d9e", background: "#0b5d9e" }}
      data-testid={testid}>
      <div className="flex items-baseline justify-between">
        <span className="font-display font-bold text-base">{label}</span>
        <span className={`font-bold text-lg ${negative ? "text-[#FCA5A5]" : ""}`}>
          {negative ? `(${fmtMoney(Math.abs(amount))})` : fmtMoney(amount)}
        </span>
      </div>
      {margin !== undefined && (
        <div className="text-[10px] uppercase tracking-widest text-white/80 mt-1">
          {margin}% {marginLabel}
        </div>
      )}
    </div>
  );
}

function NetIncomeRow({ amount, margin }) {
  const { money } = useCompactMoney();
  const negative = amount < 0;
  return (
    <div className="mt-5 py-4 px-5 text-white" style={{ background: "#0b5d9e" }} data-testid="is-net-income">
      <div className="flex items-baseline justify-between">
        <span className="font-display font-bold text-lg uppercase tracking-wider">Net Income</span>
        <span className="font-bold text-2xl">
          {negative ? `(${money(Math.abs(amount))})` : money(amount)}
        </span>
      </div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-white/80 mt-1">
        {margin}% net margin
      </div>
    </div>
  );
}

/* ----- Comparative row components ----- */
function CmpSectionHeader({ label }) {
  return (
    <tr>
      <td colSpan={5} className="text-[10px] uppercase tracking-[0.25em] text-white pt-2 pb-2 px-4" style={{ background: "#0b5d9e" }}>
        {label}
      </td>
    </tr>
  );
}

function CmpLine({ label, cell, negate = false }) {
  const sign = negate ? -1 : 1;
  return (
    <tr className="text-sm">
      <td className="py-1.5 px-2">{label}</td>
      <td className="py-1.5 px-2 text-right">{fmtCell(sign * cell.current)}</td>
      <td className="py-1.5 px-2 text-right text-[var(--ll-ink-soft)]">{fmtCell(sign * cell.prior)}</td>
      <td className="py-1.5 px-2 text-right text-xs">{fmtCell(sign * cell.change)}</td>
      <td className="py-1.5 px-2 text-right"><DeltaPct pct={cell.change_pct} negate={negate} /></td>
    </tr>
  );
}

function CmpSubtotal({ label, cell, testid, negate = false }) {
  const sign = negate ? -1 : 1;
  return (
    <tr className="font-medium border-t" data-testid={testid}>
      <td className="py-2 px-2 border-t" style={{ borderColor: "var(--ll-border)" }}>{label}</td>
      <td className="py-2 px-2 text-right border-t" style={{ borderColor: "var(--ll-border)" }}>{fmtCell(sign * cell.current)}</td>
      <td className="py-2 px-2 text-right text-[var(--ll-ink-soft)] border-t" style={{ borderColor: "var(--ll-border)" }}>{fmtCell(sign * cell.prior)}</td>
      <td className="py-2 px-2 text-right text-xs border-t" style={{ borderColor: "var(--ll-border)" }}>{fmtCell(sign * cell.change)}</td>
      <td className="py-2 px-2 text-right border-t" style={{ borderColor: "var(--ll-border)" }}><DeltaPct pct={cell.change_pct} negate={negate} /></td>
    </tr>
  );
}

function CmpTotal({ label, cell, margin, marginLabel, testid }) {
  return (
    <tr data-testid={testid}>
      <td colSpan={5} className="p-0">
        <div className="my-3 px-3 py-3 border-y-2 text-white" style={{ borderColor: "#0b5d9e", background: "#0b5d9e" }}>
          <table className="w-full">
            <tbody>
              <tr className="text-base">
                <td className="font-display font-bold">{label}</td>
                <td className="text-right font-bold w-[15%]">{fmtCell(cell.current)}</td>
                <td className="text-right text-white/70 w-[15%]">{fmtCell(cell.prior)}</td>
                <td className="text-right text-sm w-[15%]">{fmtCell(cell.change)}</td>
                <td className="text-right w-[15%]"><DeltaPct pct={cell.change_pct} darkBg /></td>
              </tr>
              {margin && (
                <tr>
                  <td colSpan={5} className="text-[10px] uppercase tracking-widest text-white/80 pt-1">
                    {margin.current}% {marginLabel} (vs {margin.prior}% prior · {margin.change_pp >= 0 ? "+" : ""}{margin.change_pp} pp)
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
}

function CmpNetIncomeRow({ cell, margin }) {
  return (
    <tr data-testid="is-net-income">
      <td colSpan={5} className="p-0">
        <div className="mt-4 px-4 py-4 text-white" style={{ background: "#0b5d9e" }}>
          <table className="w-full">
            <tbody>
              <tr>
                <td className="font-display font-bold uppercase tracking-wider">Net Income</td>
                <td className="text-right font-bold text-xl w-[15%]">{fmtCell(cell.current)}</td>
                <td className="text-right text-white/70 w-[15%]">{fmtCell(cell.prior)}</td>
                <td className="text-right text-sm w-[15%]">{fmtCell(cell.change)}</td>
                <td className="text-right w-[15%]"><DeltaPct pct={cell.change_pct} darkBg /></td>
              </tr>
              {margin && (
                <tr>
                  <td colSpan={5} className="text-[10px] uppercase tracking-[0.25em] opacity-70 pt-1">
                    {margin.current}% net margin (vs {margin.prior}% prior · {margin.change_pp >= 0 ? "+" : ""}{margin.change_pp} pp)
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </td>
    </tr>
  );
}

/* ----- Tiny formatting helpers ----- */
function fmtCell(amount) {
  if (amount === 0 || amount === null || amount === undefined) return "—";
  const neg = amount < 0;
  return neg ? `(${fmtMoney(Math.abs(amount))})` : fmtMoney(amount);
}

function DeltaPct({ pct, negate = false, darkBg = false }) {
  if (pct === null || pct === undefined) {
    return <span className={darkBg ? "text-white/40" : "text-[var(--ll-ink-muted)]"}>—</span>;
  }
  // For expense lines we negate semantically: a 10% INCREASE in expense is bad (red).
  const semanticUp = negate ? pct < 0 : pct > 0;
  const Icon = pct === 0 ? Minus : (semanticUp ? TrendingUp : TrendingDown);
  const color = pct === 0 ? (darkBg ? "text-white/60" : "text-[var(--ll-ink-muted)]")
    : semanticUp ? "text-[#059669]" : "text-[var(--ll-error)]";
  const sign = pct > 0 ? "+" : "";
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs ${color}`}>
      <Icon size={11} strokeWidth={2.5} />{sign}{pct}%
    </span>
  );
}
